import { BffHttpError, BffUnauthenticatedError } from "@/shared/api/client";
import { requireAdvisor } from "@/shared/api/identity";
import { deriveDobAge, type ClientPortfolioSummary } from "@/domain/clients/client-detail";
import {
  listCorporationClients,
  type CorporationClientSubject,
} from "@/domain/clients/corporation-client";
import {
  fetchAdvisorClientRows,
  listAdvisorIndividuals,
  type IndividualItem,
} from "@/domain/clients/individuals";
import { rememberIndividualClientId } from "@/domain/clients/individual-client";
import type { ClientListItem, ClientListResponse } from "@/domain/clients/list-types";
import {
  cachedCorporatePortfolioSummary,
  cachedPortfolioSummary,
  mapWithConcurrency,
  PORTFOLIO_FANOUT_CONCURRENCY,
} from "@/domain/clients/portfolio-fanout";

export type ClientListFault = "expired";

export type ClientListOutcome =
  | { ok: true; value: ClientListResponse }
  | { ok: false; fault: ClientListFault | null };

export async function fetchClientList(advisorId?: string): Promise<ClientListOutcome> {
  const identities = await fetchClientIdentities(advisorId);
  if (!identities.ok) return identities;
  const totals = await withClientTotals(identities.value.clients);
  if (!totals.ok) return totals;
  return { ok: true, value: { ...identities.value, clients: totals.value } };
}

export async function fetchClientIdentities(advisorId?: string): Promise<ClientListOutcome> {
  try {
    const financialAdvisorId = advisorId ?? (await requireAdvisor()).financialAdvisorId;
    const [rows, subjects] = await Promise.all([
      fetchAdvisorClientRows(financialAdvisorId),
      corporationClientsOrNone(financialAdvisorId),
    ]);
    const individuals = await listAdvisorIndividuals(rows.live);
    for (const row of rows.live) rememberIndividualClientId(row.individualId, row.icId);
    const icIdByIndividual = new Map(rows.live.map((r) => [r.individualId, r.icId] as const));
    const sinceByIndividual = new Map(
      rows.live.map((r) => [r.individualId, r.startAt ?? null] as const),
    );
    return {
      ok: true,
      value: {
        clients: [
          ...individuals.map((i) => individualRow(i, icIdByIndividual, sinceByIndividual, null)),
          ...subjects.map((subject) => corporateRow(subject, null)),
        ],
        endedIds: rows.ended.map((r) => r.individualId),
        endedIcIds: rows.ended.map((r) => r.icId),
      },
    };
  } catch (err) {
    return failed(err);
  }
}

export async function withClientTotals(
  rows: readonly ClientListItem[],
): Promise<
  | { ok: true; value: ClientListItem[]; missing: ClientListItem[] }
  | { ok: false; fault: ClientListFault | null }
> {
  try {
    const missing: ClientListItem[] = [];
    const value = await mapWithConcurrency(rows, PORTFOLIO_FANOUT_CONCURRENCY, async (row) => {
      const read = await summaryRead(
        () =>
          row.kind === "individual"
            ? cachedPortfolioSummary(row.id)
            : cachedCorporatePortfolioSummary(row.id),
        row.kind === "individual" ? "client" : "corporate client",
      );
      if (!read.ok) missing.push(row);
      return { ...row, ...summaryFields(read.ok ? read.summary : null) };
    });
    return { ok: true, value, missing };
  } catch (err) {
    return failed(err);
  }
}

export function individualRowOf(
  individual: IndividualItem,
  link: { icId: string; startAt?: string | null },
): ClientListItem {
  return individualRow(
    individual,
    new Map([[individual.id, link.icId]]),
    new Map([[individual.id, link.startAt ?? null]]),
    null,
  );
}

export function corporateRowOf(subject: CorporationClientSubject): ClientListItem {
  return corporateRow(subject, null);
}

async function corporationClientsOrNone(
  financialAdvisorId: string,
): Promise<CorporationClientSubject[]> {
  try {
    return await listCorporationClients(financialAdvisorId);
  } catch (err) {
    if (err instanceof BffUnauthenticatedError) throw err;
    console.warn(
      "clients list: corporate client list failed — individuals only:",
      err instanceof Error ? err.message : "unknown",
    );
    return [];
  }
}

function failed(err: unknown): { ok: false; fault: ClientListFault | null } {
  const fault = faultOf(err);
  if (fault === null) {
    console.error("clients list error:", err instanceof Error ? err.message : "unknown");
  } else {
    console.error(`clients list: ${fault} — ${err instanceof Error ? err.message : "unknown"}`);
  }
  return { ok: false, fault };
}

function individualRow(
  i: IndividualItem,
  icIdByIndividual: ReadonlyMap<string, string>,
  sinceByIndividual: ReadonlyMap<string, string | null>,
  summary: ClientPortfolioSummary | null,
): ClientListItem {
  const dobAge = deriveDobAge(i.personId);
  return {
    kind: "individual",
    id: i.id,
    icId: icIdByIndividual.get(i.id) ?? null,
    partyId: i.id,
    orgNumber: null,
    name: i.fullName,
    personId: i.personId,
    email: i.email,
    dob: dobAge?.dob ?? null,
    age: dobAge?.age ?? null,
    since: sinceByIndividual.get(i.id) ?? null,
    ...summaryFields(summary),
  };
}

function corporateRow(
  subject: CorporationClientSubject,
  summary: ClientPortfolioSummary | null,
): ClientListItem {
  return {
    kind: "corporation",
    id: subject.corporationClientId,
    icId: null,
    partyId: subject.corporationId,
    name: subject.name,
    orgNumber: subject.organisationNumber,
    personId: "-",
    email: null,
    dob: null,
    age: null,
    since: subject.startAt ?? null,
    ...summaryFields(summary),
  };
}

async function summaryRead(
  read: () => Promise<ClientPortfolioSummary | null>,
  what: string,
): Promise<{ ok: true; summary: ClientPortfolioSummary | null } | { ok: false }> {
  try {
    return { ok: true, summary: await read() };
  } catch (err) {
    if (faultOf(err) !== null) throw err;
    console.warn(
      `clients list: portfolio enrich failed for one ${what}:`,
      err instanceof Error ? err.message : "unknown",
    );
    return { ok: false };
  }
}

function summaryFields(summary: ClientPortfolioSummary | null) {
  return {
    totalValue: summary?.totalValue,
    returnPct: summary?.returnPct,
    volatilityPct: summary?.volatilityPct,
    cvarPct: summary?.cvarPct,
    riskBandKey: summary?.riskBandKey ?? null,
    portfolioCount: summary?.portfolios.length ?? 0,
    portfolios: (summary?.portfolios ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      value: p.value,
      returnPct: p.returnPct,
      riskBandKey: p.riskBandKey,
    })),
  };
}

function faultOf(err: unknown): ClientListFault | null {
  if (err instanceof BffUnauthenticatedError) return "expired";
  if (err instanceof BffHttpError && err.status === 401) return "expired";
  return null;
}
